export interface EntityRouteInput {
  pluginId?: string;
  entityType?: string;
  entityId?: string;
}

type EntityRouteFn = (entityId: string) => string;

const REGISTRY = new Map<string, EntityRouteFn>();

const keyOf = (pluginId: string, entityType: string): string =>
  `${pluginId}:${entityType}`;

/**
 * Each UI plugin registers its own entity-to-URL mappings on import:
 *
 *   // orders-ui/src/index.ts
 *   registerEntityRoute('orders', 'salesOrder', (id) => `/orders/sales/${id}`);
 *
 * ai-core-ui's `entityRouteFor` consumes the merged registry — so adding a
 * new plugin with a new entity type doesn't require editing ai-core-ui.
 */
export const registerEntityRoute = (
  pluginId: string,
  entityType: string,
  buildUrl: EntityRouteFn
): void => {
  REGISTRY.set(keyOf(pluginId, entityType), buildUrl);
};

export const registerEntityRoutes = (
  pluginId: string,
  routes: Record<string, EntityRouteFn>
): void => {
  for (const [entityType, fn] of Object.entries(routes)) {
    registerEntityRoute(pluginId, entityType, fn);
  }
};

export const entityRouteFor = (scope: EntityRouteInput): string | null => {
  if (!scope.entityId || !scope.pluginId || !scope.entityType) return null;
  const fn = REGISTRY.get(keyOf(scope.pluginId, scope.entityType));
  return fn ? fn(scope.entityId) : null;
};

/** All plugin IDs that have registered at least one entity route. Used by
 *  the Insights page's plugin-filter dropdown so it only lists plugins
 *  actually installed in this build. */
export const getRegisteredPluginIds = (): string[] => {
  const ids = new Set<string>();
  for (const key of REGISTRY.keys()) {
    const [pluginId] = key.split(':');
    if (pluginId) ids.add(pluginId);
  }
  return Array.from(ids).sort();
};

/** Test-only: clear the registry between test runs. */
export const __resetEntityRoutes = (): void => {
  REGISTRY.clear();
};
